import { userAtom } from '@/atoms/user.atom';
import { JobParamsPanel } from '@/components/JobParamsPanel';
import { ProcessJob } from '@/graphql/graphql';
import { isInternalUser } from '@/lib/email';
import { flattenJobParams, type JobParamTranslate } from '@/lib/jobParams';
import { cn } from '@/lib/utils';
import { useAtomValue } from 'jotai';
import { ChevronRight } from 'lucide-react';
import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "What did this run with?" — the settings a process job was started with,
 * read back out of its `inputJson`.
 *
 * Staff-only (`isInternalUser`): the payload also carries S3 keys and internal
 * ids, and the labels lean on run-dialog wording that assumes you know the
 * feature. The gate is presentational — `inputJson` has always been on the
 * wire for anyone who can see the batch — so this is a convenience for support,
 * not an access control. The list itself is `JobParamsPanel`, shared with the
 * Analyses run-details drawer.
 */
const JobParams: FunctionComponent<{ job: Partial<ProcessJob> }> = ({ job }) => {
  const t = useTranslation('project').t as JobParamTranslate;
  const user = useAtomValue(userAtom);
  const [open, setOpen] = useState(false);

  const { params, internals } = useMemo(() => flattenJobParams(job.inputJson), [job.inputJson]);

  if (!isInternalUser(user?.email)) return null;
  if (params.length === 0 && internals.length === 0) return null;

  return (
    <div className='mt-1 ml-6'>
      <button
        type='button'
        data-testid='job-params-toggle'
        onClick={() => setOpen((v) => !v)}
        className='flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 dark:text-stone-400 dark:hover:text-stone-100'
      >
        <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
        {t('batch.parameters')}
        <span className='opacity-60'>({params.length})</span>
      </button>

      {open && (
        <div className='mt-1'>
          <JobParamsPanel inputJson={job.inputJson} type={job.type} staffTools />
        </div>
      )}
    </div>
  );
};

export default JobParams;
